import { PRODUCT_VERSION } from '../lib/version';
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, sheetsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import PageHeader from '../components/PageHeader';
import { TableScroll } from '../components/ResponsiveTable';
import usePageTitle from '../hooks/usePageTitle';
import { toast } from 'sonner';
import { Settings, User, Lock, Zap, FileSpreadsheet, Link, Unlink, Plus, Trash2, CheckCircle, AlertCircle, KeyRound, ChevronRight, Database } from 'lucide-react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import useChatsSummary from '../hooks/useChatsSummary';

/* Aurora (2026-08-23): labels above values in the small fact tiles, numbers tabular. */
const TILE = 'rounded-xl bg-secondary/60 p-4 min-w-0';
const TILE_LABEL = 'mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground';
const FIELD = 'bg-secondary/60 border-border placeholder:text-muted-foreground/70';

export default function SettingsPage() {
  usePageTitle('Settings');
  const { user } = useAuth();
  // r16p: the "API access" card shows once the chat routes answer (one shared 60 s query, hooks/useChatsSummary)
  const chatsSummary = useChatsSummary();
  const chatsAvail = chatsSummary.available;
  // r16pm review: ... and also where only the key LEVERS answer (403 no_chat_access, 404 chats_disabled): list, revoke, Lock
  const keysAvail = chatsSummary.levers;
  const queryClient = useQueryClient();
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();

  // Check for sheets=connected query param (from OAuth callback)
  useEffect(() => {
    if (searchParams.get('sheets') === 'connected') {
      toast.success('Google Sheets connected successfully!');
      queryClient.invalidateQueries(['sheetsStatus']);
      queryClient.invalidateQueries(['sheetsConfigs']);
      // Clean up URL
      window.history.replaceState({}, '', '/settings');
    }
  }, [searchParams, queryClient]);

  const { data: sheetsStatus, isLoading: sheetsStatusLoading } = useQuery({
    queryKey: ['sheetsStatus'],
    queryFn: () => sheetsApi.getStatus().then(res => res.data),
  });

  const { data: sheetsConfigs, isLoading: sheetsConfigsLoading } = useQuery({
    queryKey: ['sheetsConfigs'],
    queryFn: () => sheetsApi.getConfigs().then(res => res.data),
    enabled: sheetsStatus?.connected,
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data) => {
      console.log('Updating profile with data:', data);
      return adminApi.updateProfile(data);
    },
    onSuccess: (response) => {
      console.log('Profile update success:', response);
      queryClient.invalidateQueries(['profile']);
      if (response?.data?.password_changed) {
        toast.success('Password changed successfully');
      } else {
        toast.success('Profile updated successfully');
      }
    },
    onError: (error) => {
      console.error('Profile update error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    },
  });

  const connectSheetsMutation = useMutation({
    mutationFn: sheetsApi.startLogin,
    onSuccess: (response) => {
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to start Google connection');
    },
  });

  const disconnectSheetsMutation = useMutation({
    mutationFn: sheetsApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries(['sheetsStatus']);
      queryClient.invalidateQueries(['sheetsConfigs']);
      toast.success('Google Sheets disconnected');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    },
  });

  const createConfigMutation = useMutation({
    mutationFn: sheetsApi.createConfig,
    onSuccess: () => {
      queryClient.invalidateQueries(['sheetsConfigs']);
      setConfigDialogOpen(false);
      toast.success('Sheet configuration added');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to add configuration');
    },
  });

  const deleteConfigMutation = useMutation({
    mutationFn: sheetsApi.deleteConfig,
    onSuccess: () => {
      queryClient.invalidateQueries(['sheetsConfigs']);
      toast.success('Configuration deleted');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete configuration');
    },
  });

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwords.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    updateProfileMutation.mutate({ password: passwords.new });
    setPasswords({ current: '', new: '', confirm: '' });
  };

  const handleAddConfig = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    createConfigMutation.mutate({
      name: formData.get('name'),
      spreadsheet_id: formData.get('spreadsheet_id'),
      sheet_name: formData.get('sheet_name') || 'Sheet1',
      purpose: formData.get('purpose'),
    });
  };

  const getPurposeBadge = (purpose) => {
    const variants = {
      STATS_EXPORT: { color: 'bg-primary/15 text-primary border-primary/30', label: 'Stats Export' },
      ACCOUNTS_IMPORT: { color: 'bg-success/15 text-success border-success/30', label: 'Accounts Import' },
      SETTINGS_IMPORT: { color: 'bg-warning/15 text-warning border-warning/30', label: 'Settings Import' },
    };
    const v = variants[purpose] || { color: 'bg-muted/60 text-muted-foreground border-border', label: purpose };
    return <Badge className={`${v.color} border`}>{v.label}</Badge>;
  };

  /* The phone card list (< md) mirrors the table; it carries its own testids so the table's stay
   * unique in the DOM (both are rendered, one is CSS-hidden by TableScroll). */
  const sheetsMobileList = sheetsConfigs && sheetsConfigs.length > 0 ? (
    sheetsConfigs.map((config) => (
      <div key={config._id} className="rounded-xl bg-secondary/60 p-3" data-testid="sheet-config-card">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium">{config.name}</p>
            <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{config.spreadsheet_id}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => deleteConfigMutation.mutate(config._id)}
            className="tap h-8 w-8 shrink-0 text-danger hover:bg-danger/10 hover:text-danger"
            aria-label={`Delete ${config.name}`}
            data-testid="delete-config-card-button"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {getPurposeBadge(config.purpose)}
          <span className="truncate">Sheet: <span className="text-foreground">{config.sheet_name}</span></span>
        </div>
      </div>
    ))
  ) : null;

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <PageHeader
        icon={Settings}
        title="Settings"
        description="Manage your account, integrations, and preferences"
      />

      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        {/* Account Info */}
        <Card className="aurora-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
              <User className="h-5 w-5 text-primary" />
              Account Information
            </CardTitle>
            <CardDescription>Your account details and subscription</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={user?.email || ''}
                disabled
                className={FIELD}
                data-testid="account-email-input"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:gap-4">
              <div className={TILE}>
                <p className={TILE_LABEL}>Plan</p>
                <p className="truncate font-semibold capitalize text-primary" data-testid="account-plan">{user?.plan || 'Free'}</p>
              </div>
              <div className={TILE}>
                <p className={TILE_LABEL}>Max Slots</p>
                <p className="font-semibold tnum" data-testid="account-max-slots">{user?.max_slots || 5}</p>
              </div>
            </div>
            <div className={TILE}>
              <p className={TILE_LABEL}>Account Created</p>
              <p className="font-semibold tnum">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="aurora-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
              <Lock className="h-5 w-5 text-primary" />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwords.new}
                  onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                  placeholder="Enter new password"
                  className={FIELD}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  placeholder="Confirm new password"
                  className={FIELD}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                className="tap w-full"
                disabled={updateProfileMutation.isPending}
                data-testid="update-password-button"
              >
                Update Password
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* r16p (design 7.4): the card to /settings/api - drawn only while the r16p switch is ON and the chat routes answer, or (r16pm
            review) only the key levers do: a no-chat-access login or chats switched off can still list, revoke and Lock */}
        {keysAvail && (
          <Card className="aurora-card" data-testid="settings-api-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
                <KeyRound className="h-5 w-5 text-primary" />
                API access
              </CardTitle>
              <CardDescription>Keys for your own platform or AI to read your slots&apos; chats and queue replies</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground text-pretty">
                Create a key per integration, give it only the slots and permissions it needs, and revoke it the moment you stop using it.
                &quot;Lock API access&quot; revokes them all at once.
              </p>
              <Button asChild variant="outline" className="tap rounded-full">
                <RouterLink to="/settings/api/keys" data-testid="settings-api-link">Open API access<ChevronRight className="h-4 w-4" /></RouterLink>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* r16pm (design 6.6 / 6.10, O8): the card to /settings/chats - the same gate as the API access card */}
        {chatsAvail && (
          <Card className="aurora-card" data-testid="settings-chatdata-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
                <Database className="h-5 w-5 text-primary" />
                Chat data
              </CardTitle>
              <CardDescription>How long DM text is kept, and erasing one slot&apos;s chat data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground text-pretty">
                Message text is erased from the server after the period you choose. One slot&apos;s stored chats can be erased at once.
              </p>
              <Button asChild variant="outline" className="tap rounded-full">
                <RouterLink to="/settings/chats" data-testid="settings-chatdata-link">Open chat data<ChevronRight className="h-4 w-4" /></RouterLink>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Google Sheets Integration */}
        <Card className="aurora-card lg:col-span-2">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
                  <FileSpreadsheet className="h-5 w-5 text-success" />
                  Google Sheets Integration
                </CardTitle>
                <CardDescription className="mt-1.5">Connect Google Sheets for data import/export</CardDescription>
              </div>
              {sheetsStatus?.connected ? (
                <div className="flex min-w-0 flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">
                  <Badge className="max-w-full border border-success/30 bg-success/15 text-success">
                    <CheckCircle className="mr-1 h-3 w-3 shrink-0" />
                    <span className="truncate">Connected as {sheetsStatus.email}</span>
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => disconnectSheetsMutation.mutate()}
                    className="tap text-danger hover:bg-danger/10 hover:text-danger"
                    data-testid="disconnect-sheets-button"
                  >
                    <Unlink className="h-4 w-4 mr-1" />
                    Disconnect
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => connectSheetsMutation.mutate()}
                  className="tap shadow-glow sm:shrink-0"
                  disabled={connectSheetsMutation.isPending || !sheetsStatus?.configured}
                  data-testid="connect-sheets-button"
                >
                  <Link className="h-4 w-4 mr-2" />
                  {connectSheetsMutation.isPending ? 'Connecting...' : 'Connect Google Sheets'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {sheetsStatusLoading ? (
              <div className="space-y-3" aria-busy="true" aria-label="Loading Google Sheets status" data-testid="sheets-status-skeleton">
                <div className="h-5 w-40 animate-pulse rounded-xl bg-secondary/60" />
                <div className="h-24 animate-pulse rounded-xl bg-secondary/60" />
              </div>
            ) : !sheetsStatus?.configured ? (
              <div className="rounded-xl border border-warning/20 bg-warning/10 p-6 text-center">
                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-warning" />
                <p className="font-medium text-warning">Google OAuth Not Configured</p>
                <p className="mx-auto mt-1 max-w-prose text-sm text-muted-foreground text-pretty">
                  Google Sheets integration requires OAuth credentials to be configured on the server.
                  Contact your administrator to set up GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET.
                </p>
              </div>
            ) : sheetsStatus?.connected ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="font-heading text-base font-semibold">Configured Sheets</h4>
                  <Button
                    size="sm"
                    onClick={() => setConfigDialogOpen(true)}
                    className="tap"
                    data-testid="add-sheet-config-button"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Sheet
                  </Button>
                </div>
                {sheetsConfigsLoading ? (
                  <div className="space-y-2" aria-busy="true" aria-label="Loading sheet configurations" data-testid="sheets-configs-skeleton">
                    {[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-secondary/60" />)}
                  </div>
                ) : sheetsConfigs && sheetsConfigs.length > 0 ? (
                  <TableScroll mobile={sheetsMobileList}>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead>Name</TableHead>
                          <TableHead>Purpose</TableHead>
                          <TableHead>Spreadsheet ID</TableHead>
                          <TableHead>Sheet</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sheetsConfigs.map((config) => (
                          <TableRow key={config._id} className="border-border" data-testid="sheet-config-row">
                            <TableCell className="max-w-[200px] truncate font-medium">{config.name}</TableCell>
                            <TableCell>{getPurposeBadge(config.purpose)}</TableCell>
                            <TableCell className="max-w-[200px] truncate font-mono text-xs">
                              {config.spreadsheet_id}
                            </TableCell>
                            <TableCell className="max-w-[160px] truncate">{config.sheet_name}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteConfigMutation.mutate(config._id)}
                                className="tap h-8 w-8 text-danger hover:bg-danger/10 hover:text-danger"
                                aria-label={`Delete ${config.name}`}
                                data-testid="delete-config-button"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableScroll>
                ) : (
                  <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10">
                    <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                      <FileSpreadsheet className="h-5 w-5" />
                    </span>
                    <p className="mt-3 font-medium">No sheets configured yet</p>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">Add a sheet configuration to start syncing data</p>
                    <Button
                      variant="outline"
                      onClick={() => setConfigDialogOpen(true)}
                      className="tap mt-5 rounded-full"
                      data-testid="add-sheet-config-empty-button"
                    >
                      <Plus className="h-4 w-4" /> Add Sheet
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10">
                <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
                  <FileSpreadsheet className="h-5 w-5" />
                </span>
                <p className="mt-3 font-medium">Not Connected</p>
                <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">Connect your Google account to import/export data with Google Sheets</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* System Info */}
        <Card className="aurora-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-heading text-base font-semibold">
              <Zap className="h-5 w-5 text-primary" />
              System Information
            </CardTitle>
            <CardDescription>Server and version details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={TILE}>
              <p className={TILE_LABEL}>Version</p>
              <p className="font-semibold tnum">{PRODUCT_VERSION}</p>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-success/20 bg-success/10 p-4">
              <span className="h-2 w-2 shrink-0 rounded-full bg-success" aria-hidden />
              <p className="text-sm text-success">
                <strong>Status:</strong> All systems operational
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Sheet Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto rounded-lg sm:w-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Sheet Configuration</DialogTitle>
            <DialogDescription>
              Configure a Google Sheet for data sync operations.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddConfig} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="config_name">Configuration Name</Label>
              <Input
                id="config_name"
                name="name"
                placeholder="My Stats Sheet"
                className={FIELD}
                required
                data-testid="config-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="spreadsheet_id">Spreadsheet ID</Label>
              <Input
                id="spreadsheet_id"
                name="spreadsheet_id"
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                className={`${FIELD} font-mono text-sm`}
                required
                data-testid="spreadsheet-id-input"
              />
              <p className="text-xs text-muted-foreground break-words">
                Find this in your Google Sheet URL: docs.google.com/spreadsheets/d/[SPREADSHEET_ID]/edit
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sheet_name">Sheet Name</Label>
                <Input
                  id="sheet_name"
                  name="sheet_name"
                  placeholder="Sheet1"
                  defaultValue="Sheet1"
                  className={FIELD}
                  data-testid="sheet-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purpose">Purpose</Label>
                <Select name="purpose" required defaultValue="STATS_EXPORT">
                  <SelectTrigger className="bg-secondary/60 border-border" data-testid="purpose-select">
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STATS_EXPORT">Stats Export</SelectItem>
                    <SelectItem value="ACCOUNTS_IMPORT">Accounts Import</SelectItem>
                    <SelectItem value="SETTINGS_IMPORT">Settings Import</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setConfigDialogOpen(false)} className="tap">
                Cancel
              </Button>
              <Button
                type="submit"
                className="tap"
                disabled={createConfigMutation.isPending}
                data-testid="add-config-submit-button"
              >
                {createConfigMutation.isPending ? 'Adding...' : 'Add Configuration'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
